
// 定义数据类型
type DataType = 'String' | 'Number' | 'Boolean' | 'Object' | 'Array';
type FallbackFunction = (value: string | null) => any;

export class Storage {
  private key: string

  private type?: DataType

  private fallback?: any | FallbackFunction

  /**
   * 生成存储对象的构造函数
   * @param {string} key localStorage 中的 key 值
   * @param {DataType} type 数据类型，如 Object
   * @param {any} fallback 解析失败时返回的默认值
   */
  constructor(key: string, type?: DataType, fallback?: any | FallbackFunction) {
    this.key = key
    this.type = type
    this.fallback = fallback
  }

  /**
   * 获取 localStorage 中的数据，不存在则返回 fallback 的值或 null
   * @returns {any} localStorage 中的数据
   */
  get(): any {
    const localStorageStr = window.localStorage.getItem(this.key)

    if (localStorageStr === 'undefined') {
      return undefined
    } else if (localStorageStr !== null) {
      try {
        const result = JSON.parse(localStorageStr)
        if (!this.type) {
          return result
        } else if (Object.prototype.toString.call(result) === `[object ${this.type}]`) {
          return result
        }
      } catch (err) {
        if (err instanceof Error) {
          console.error(`字符串 ${localStorageStr} 解析失败: ${err.message}`)
        } else {
          console.error(`字符串 ${localStorageStr} 解析失败`)
          console.error(err)
        }
      }
    }

    if (typeof this.fallback === 'undefined') {
      return null
    } else if (typeof this.fallback === 'function') {
      return this.fallback(localStorageStr)
    }
    return this.fallback
  }

  /**
   * 将数据存储到 localStorage 中
   * @param {any} value 需要保存到 localStorage 的数据
   */
  set(value: any | ((prevValue: any) => any)): void {
    if (typeof value === 'function') {
      const oldValue = this.get()
      const newValue = value(oldValue)
      this.set(newValue)
      return
    }

    const type = Object.prototype.toString.call(value)
    if (this.type && type !== `[object ${this.type}]`) {
      const matchedType = type.replace(/^\[object (\S+)\]$/, '$1')
      throw new Error(`${this.key} 类型不匹配，期待类型：${this.type}，传入的类型：${matchedType}`)
    }

    window.localStorage.setItem(this.key, JSON.stringify(value))
  }

  /**
   * 删除key对应的数据
   */
  remove(): void {
    window.localStorage.removeItem(this.key)
  }

  /**
   * 获取 localStorage 中的数据，不存在则返回 fallback 的值或 null
   * @param {string} key localStorage 中的 key 值
   * @param {DataType} type 数据类型，如 Object
   * @param {any} fallback 解析失败时返回的默认值
   * @returns {any} localStorage 中的数据
   */
  static get(key: string, type?: DataType, fallback?: any): any {
    const storage = new Storage(key, type, fallback)
    return storage.get()
  }

  /**
   * 将数据存储到 localStorage 中
   * @param {string} key localStorage 中的 key 值
   * @param {any} value 需要保存到 localStorage 的数据
   */
  static set(key: string, value: any): void {
    const storage = new Storage(key)
    storage.set(value)
  }

  /**
   * 删除key对应的数据
   * @param {string} key 保存数据的键
   */
  static remove(key: string): void {
    const storage = new Storage(key)
    storage.remove()
  }
}
